import React, { useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import { Link, useHistory } from 'react-router-dom';
import { Button, Checkbox, Container, Dimmer, Dropdown, Form, Icon, Loader, Pagination, Table } from 'semantic-ui-react';
import { formatDate, formatDateTime, parseStatus } from '../../../utils/shared.utils';
import { parsePaymentChannel, paymentChannelClassName, paymentChannelOptions } from '../../../constants/payment-channel';
import { printUnprintedDeliveryReceiptBatch } from '../../utils/deliveryReceiptBatchPrint';
import { ADMIN_LIST_PAGINATION_ROW_CLASS } from '../list/list';
import OrderDeliveryComponent from '../order/order.delivery';
import OrderEditModal, { BANK_PAID_EDIT_LOCKED_MSG } from '../order/order.edit.modal';
import {
  canCreatePapaDeliveryForOrder,
  canSelectDeliveryForPapa,
  createPapaDeliveries,
  fetchPapaDistricts,
  PapaDistrict,
} from '../order/order.papa-actions';
import OrderModel from '../order/order.model';
import './order.report.scss';

function aggregateProductRows(orders: any[]): { name: string; count: number }[] {
  const map = new Map<string, { name: string; count: number }>();
  for (const order of orders) {
    for (const delivery of order.deliveries || []) {
      for (const item of delivery.items || []) {
        const pid = String(item.product?.id ?? '');
        const name = item.product?.name ? String(item.product.name) : 'Бараа';
        const key = pid ? `id:${pid}` : `n:${name}`;
        const c = Number(item.count) || 0;
        const prev = map.get(key);
        if (prev) prev.count += c;
        else map.set(key, { name, count: c });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'mn'));
}

function totalPieces(rows: { count: number }[]): number {
  return rows.reduce((sum, r) => sum + r.count, 0);
}

function ReportOrders(props: any) {

  const history = useHistory()
  const [orders, setOrders] = useState<any[]>([])
  const [page, setPage] = useState({ page: 1, size: 20, totalPages: 1 })
  const [loading, setLoading] = useState(true)
  const [batchPrinting, setBatchPrinting] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [successMsg, setSuccessMsg] = useState("")
  const [fetchedOrders, setFetchedOrders] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)
  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setMonth(monthAgo.getMonth() - 1);
  const [fromDate, setFromDate] = useState<string>(formatDate(monthAgo))
  const [toDate, setToDate] = useState<string>(formatDate(today))
  const [totalOrderCount, setTotalOrderCount] = useState<number>(0)
  const [totalAmount, setTotalAmount] = useState<number>(0)
  const [totalDeliveryCost, setTotalDeliveryCost] = useState<number>(0)
  const [totalProductPieces, setTotalProductPieces] = useState<number>(0)
  const [unprintedOnly, setUnprintedOnly] = useState(false)
  const [paymentChannel, setPaymentChannel] = useState<string>('')
  const [receiptNo, setReceiptNo] = useState<string>('')
  const [editOrder, setEditOrder] = useState<any>(null)
  
  const [papaSelectMode, setPapaSelectMode] = useState(false)
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<Set<number>>(new Set())
  const [papaCreating, setPapaCreating] = useState(false)
  const [papaDistricts, setPapaDistricts] = useState<PapaDistrict[]>([])
  const [papaDistrictId, setPapaDistrictId] = useState<string>('')
  const [papaDistrictsLoading, setPapaDistrictsLoading] = useState(false)

  // Fetch summary/totals for all pages (only confirmed orders)
  const fetchOrderSummary = (from: string, to: string, store: number, onlyUnprinted: boolean, channel: string, receiptToken: string) => {
    var token = localStorage.getItem('adminToken')
    if (token && token !== 'undefined') {
      // Always fetch all orders and filter by CONFIRMED status client-side
      // to ensure we only sum confirmed orders
      fetchAllPagesForTotal(from, to, store, onlyUnprinted, channel, receiptToken, token)
    }
  }
  
  // Fallback: Calculate total by fetching all orders (inefficient but works)
  const fetchAllPagesForTotal = (from: string, to: string, store: number, onlyUnprinted: boolean, channel: string, receiptToken: string, token: string) => {
    // Fetch with a large page size to get all orders at once
    fetch(process.env.REACT_APP_API_URL + 'mongol/orders', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        page: { page: 1, size: 10000 },  // Large size to get all orders
        store: store, 
        startDate: from, 
        endDate: to,
        unprintedOnly: onlyUnprinted,
        paymentChannel: channel || undefined,
        token: receiptToken || undefined,
      })
    }).then((res: any) => {
      if (res.ok) {
        res.json().then((resJson: any) => {
          const allOrders = resJson.data || [];
          const confirmedOrders = allOrders.filter((order: any) => order.status === 'CONFIRMED');
          const total = confirmedOrders.reduce((sum: number, order: any) => {
            return sum + (Number(order.totalAmount) || 0);
          }, 0);
          //test
          // Calculate total delivery cost from all order items
          const totalDelivery = confirmedOrders.reduce((sum: number, order: any) => {
            const orderItemsDeliveryCost = (order.deliveries || []).reduce((deliverySum: number, delivery: any) => {
              const itemsDeliveryCost = (delivery.items || []).reduce((itemSum: number, item: any) => {
                return itemSum + ((Number(item.deliveryCost) || 0) * (Number(item.count) || 0));
              }, 0);
              return deliverySum + itemsDeliveryCost;
            }, 0);
            return sum + orderItemsDeliveryCost;
          }, 0);
          
          setTotalAmount(total);
          setTotalDeliveryCost(totalDelivery);
          setTotalOrderCount(confirmedOrders.length);
          setTotalProductPieces(totalPieces(aggregateProductRows(confirmedOrders)));
        })
      }
    }).catch(err => {
      console.error('Error fetching totals:', err);
    })
  }

  useEffect(() => {
      if (fetchedOrders) {
        return
      }
      fetchOrders(1);
      setFetchedOrders(true)
  }, []);

  useEffect(() => {
      if (fetchedOrders) {
        setPage({ page: 1, size: pageSize, totalPages: 1 })
        fetchOrders(1)
      }
  }, [activeTabIndex]);

  const fetchOrders = (pageNum: number, size?: number, options?: { papaEligibleOnly?: boolean }) => {
    const currentPageSize = size || pageSize;
    // Use current state values
    const currentFromDate = fromDate;
    const currentToDate = toDate;
    const currentTabIndex = activeTabIndex;
    const currentPaymentChannel = paymentChannel;
    const currentReceiptNo = receiptNo.trim();
    const papaEligibleOnly = options?.papaEligibleOnly !== undefined
      ? options.papaEligibleOnly
      : papaSelectMode;
    
    var token = localStorage.getItem('adminToken')
    if (token && token !== 'undefined') {
      setLoading(true)
      // Reset totals while loading new data
      setTotalOrderCount(0)
      setTotalAmount(0)
      setTotalDeliveryCost(0)
      setTotalProductPieces(0)
      
      const requestBody: any = { 
        page: { page: pageNum, size: currentPageSize }, 
        store: currentTabIndex, 
        startDate: currentFromDate, 
        endDate: currentToDate,
        unprintedOnly: unprintedOnly,
      };
      if (papaEligibleOnly) {
        requestBody.papaEligibleOnly = true
      }
      if (currentPaymentChannel) {
        requestBody.paymentChannel = currentPaymentChannel
      }
      if (currentReceiptNo) {
        requestBody.token = currentReceiptNo
      }
      
      console.log('Fetching orders with:', requestBody);
      
      fetch(process.env.REACT_APP_API_URL + 'mongol/orders', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }).then((res: any) => {
        setLoading(false)
        if (res.ok) {
          res.json().then((resJson: any) => {
            let orders: OrderModel[] = resJson.data || [];
            for (let order of orders) {
              if (order.createdAt) {
                order.createdAt = new Date(order.createdAt)
                order['createdAtStr'] = formatDateTime(order.createdAt)
              }
              if (order.reserveUntil) {
                order.reserveUntil = new Date(order.reserveUntil)
                order['reserveDate'] = formatDateTime(order.reserveUntil)
              }
              
              // Calculate total delivery cost from all order items
              let itemsDeliveryCost = 0;
              for (let delivery of order.deliveries) {
                let date = new Date(delivery.date)
                delivery.date = `${date.getMonth() + 1} сарын ${date.getUTCDate()}`
                // Sum up deliveryCost from all items in this delivery (multiply by count)
                for (let item of delivery.items) {
                  itemsDeliveryCost += (Number(item.deliveryCost) || 0) * (Number(item.count) || 0);
                  // item.product.imageXsUrl = `https://${bucket}.s3.${region}.amazonaws.com/${item.product.imageXsUrl}`
                }
              }
              // Store calculated items delivery cost
              order.itemsDeliveryCost = itemsDeliveryCost;
            }
            // Calculate totalAmount from confirmed orders only
            // Always fetch all orders to filter by CONFIRMED status client-side
            let calculatedTotalAmount: number;
            // Sum totalAmount from confirmed orders in the current page only (for display purposes)
            calculatedTotalAmount = orders
              .filter((order) => order.status === 'CONFIRMED')
              .reduce((sum, order) => {
                const orderAmount = Number(order.totalAmount) || 0;
                return sum + orderAmount;
              }, 0);
            
            // Keep rows expanded while in Papa select mode so the delivery checkboxes
            // survive pagination / date search instead of collapsing back.
            if (papaEligibleOnly) {
              orders = orders.map((order) => Object.assign({}, order, { showItems: true }))
            }
            // Update all states together
            setOrders(orders)
            setPage((prev) => ({
              ...prev,
              page: resJson.page?.page ?? pageNum,
              totalPages: resJson.page?.totalPages ?? 1,
              totalRows: resJson.page?.totalRows,
              size: currentPageSize,
            }))
            // Don't set totalOrderCount here - it will be set by fetchOrderSummary after counting CONFIRMED orders
            
            // Always fetch all orders and filter by CONFIRMED status to get accurate total and count
            fetchOrderSummary(currentFromDate, currentToDate, currentTabIndex, unprintedOnly, currentPaymentChannel, currentReceiptNo)
            
            console.log('Updated totals:', {
              ordersCount: orders.length,
              calculatedTotalAmount,
              fromDate: currentFromDate,
              toDate: currentToDate,
              activeTabIndex: currentTabIndex,
              responseKeys: Object.keys(resJson),
              firstOrderId: orders[0]?.id,
              lastOrderId: orders[orders.length - 1]?.id,
              ordersTotalAmounts: orders.map(o => o.totalAmount)
            });
          })
        } else {
          // If response is not ok, reset totals
          setTotalOrderCount(0)
          setTotalAmount(0)
          setTotalDeliveryCost(0)
          setTotalProductPieces(0)
        }
      }).catch(err => {
        setLoading(false)
        setErrorMsg("Сүлжээний алдаа")
        setTotalOrderCount(0)
        setTotalAmount(0)
        setTotalDeliveryCost(0)
        setTotalProductPieces(0)
        console.error(err)
      })
    }
    else {
      window.alert("Та дахин нэвтэрнэ үү")
      history.push("/mongol/login")
    }
  }

  const toggle = (orderId: number) => {
    setOrders((prev) => prev.map((order) => (
      order.id === orderId ? Object.assign({}, order, { showItems: !order.showItems }) : order
    )))
  }

  const visibleOrders = useMemo(() => {
    if (!papaSelectMode) return orders || []
    return (orders || []).filter(canCreatePapaDeliveryForOrder)
  }, [orders, papaSelectMode])

  const onPageChange = (event: any, data: any) => {
      setPage((prev) => ({ ...prev, page: data.activePage }))
      fetchOrders(data.activePage)
      window.scrollTo(0, 100)
  }

  const onPageSizeChange = (event: any, data: any) => {
      const newPageSize = data.value
      setPageSize(newPageSize)
      setPage((prev) => ({ ...prev, page: 1, size: newPageSize }))
      fetchOrders(1, newPageSize)
      window.scrollTo(0, 100)
  }

  const onFilterApply = () => {
      setPage({ page: 1, size: pageSize, totalPages: 1 })
      setTotalOrderCount(0)
      setTotalAmount(0)
      setTotalDeliveryCost(0)
      setTotalProductPieces(0)
      fetchOrders(1)
  }

  const onPapaSelectToggle = (deliveryId: number, checked: boolean) => {
    setSelectedDeliveryIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(deliveryId)
      }
      else {
        next.delete(deliveryId)
      }
      return next
    })
  }

  const onCancelPapaSelect = () => {
    setPapaSelectMode(false)
    setSelectedDeliveryIds(new Set())
    setPapaDistrictId('')
    setPapaDistricts([])
    setErrorMsg("")
    setPage({ page: 1, size: pageSize, totalPages: 1 })
    fetchOrders(1, pageSize, { papaEligibleOnly: false })
  }

  const loadPapaDistricts = async () => {
    var token = localStorage.getItem('adminToken')
    if (!token || token === 'undefined') {
      return
    }

    setPapaDistrictsLoading(true)
    const result = await fetchPapaDistricts(process.env.REACT_APP_API_URL || '', token)
    setPapaDistrictsLoading(false)

    if (result.ok && result.districts) {
      setPapaDistricts(result.districts)
      return
    }

    setErrorMsg(result.error || 'Papa дүүргийн жагсаалт авахад алдаа гарлаа')
    setSuccessMsg("")
  }

  const onStartPapaSelect = () => {
    setPapaSelectMode(true)
    setSelectedDeliveryIds(new Set())
    setPapaDistrictId('')
    setErrorMsg("")
    setSuccessMsg("")
    setPage({ page: 1, size: pageSize, totalPages: 1 })
    fetchOrders(1, pageSize, { papaEligibleOnly: true })
    loadPapaDistricts()
  }

  const onCreatePapaSelected = async () => {
    if (selectedDeliveryIds.size === 0 || !papaDistrictId) {
      return
    }

    var token = localStorage.getItem('adminToken')
    if (!token || token === 'undefined') {
      window.alert("Та дахин нэвтэрнэ үү")
      history.push("/mongol/login")
      return
    }

    const selectedCount = selectedDeliveryIds.size
    setPapaCreating(true)
    setLoading(true)
    setErrorMsg("")
    setSuccessMsg("")

    const result = await createPapaDeliveries(
      process.env.REACT_APP_API_URL || '',
      token,
      Array.from(selectedDeliveryIds),
      papaDistrictId,
    )

    setPapaCreating(false)
    setLoading(false)

    if (result.ok) {
      const papaCode = result.data?.code || result.data?.id || ''
      onCancelPapaSelect()
      setSuccessMsg(
        papaCode
          ? `Papa хүргэлт амжилттай үүслээ: ${papaCode} (${selectedCount} хүргэлт)`
          : `Papa хүргэлт амжилттай үүслээ (${selectedCount} хүргэлт)`,
      )
      return
    }

    const failedSuffix = result.failedDeliveryId ? ` (#${result.failedDeliveryId})` : ''
    setSuccessMsg("")
    setErrorMsg((result.error || "Papa хүргэлт үүсгэхэд алдаа гарлаа") + failedSuffix)
  }
  const printUnprintedBatch = () => {
    const token = localStorage.getItem('adminToken');
    if (!token || token === 'undefined') {
      window.alert('Та дахин нэвтэрнэ үү');
      history.push('/mongol/login');
      return;
    }
    if (!fromDate || !toDate) {
      setErrorMsg('Эхний болон сүүлийн өдрийг сонгоно уу');
      return;
    }
    setBatchPrinting(true);
    setErrorMsg('');
    printUnprintedDeliveryReceiptBatch({
      apiUrl: process.env.REACT_APP_API_URL || '',
      token,
      store: activeTabIndex,
      orderStartDate: fromDate,
      orderEndDate: toDate,
    }).then((r) => {
      setBatchPrinting(false);
      if (r.ok) {
        fetchOrders(page.page);
      } else if (r.error === 'increment') {
        setErrorMsg('Баримтын тоо шинэчлэхэд алдаа гарлаа');
      } else if (r.error === 'network') {
        setErrorMsg('Сүлжээний алдаа');
      }
    });
  };

  return (
    <div className="page-admin">
      <Container>
        <div className="flex-div width-100">
          <Dimmer active={loading || batchPrinting}>
            <Loader size='medium' inverted />
          </Dimmer>
          <section className="admin-reports" id="my-form">

            <div className="width-100 text-center"><h2>Захиалгууд</h2></div>

            <div className="tab-head">
              <div
                className={"tab-header" + (activeTabIndex === 0 ? " selected" : "")}
                onClick={() => setActiveTabIndex(0)}
              >
                Веб
              </div>
              <div
                className={"tab-header" + (activeTabIndex === 1 ? " selected" : "")}
                onClick={() => setActiveTabIndex(1)}
              >
                Дэлгүүр
              </div>
            </div>

            <Form className="report-filter flex-div margin-top-50">
              <Form.Input label="Эхний өдөр: " type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}/>
              <Form.Input label="Сүүлийн өдөр: " type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}/>
              <Form.Input
                label="Баримтын дугаар: "
                data-testid="receipt-no-filter"
                placeholder="Огнооноос үл хамаарна"
                value={receiptNo}
                onChange={(e) => setReceiptNo(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') onFilterApply() }}
              />
              <Form.Field>
                <label>Худалдан авсан сувгийн төлөв: </label>
                <Dropdown
                  data-testid="payment-channel-filter"
                  placeholder="Бүгд"
                  selection
                  clearable
                  options={paymentChannelOptions()}
                  value={paymentChannel}
                  onChange={(_e, d) => setPaymentChannel(String(d.value || ''))}
                />
              </Form.Field>
              <Form.Field>
                <label>&nbsp;</label>
                <Checkbox
                  label="Зөвхөн хэвлээгүй баримт (0)"
                  checked={unprintedOnly}
                  onChange={(_e, d) => setUnprintedOnly(!!d.checked)}
                />
              </Form.Field>
              <Form.Field className="report-filter-actions">
                <label>&nbsp;</label>
                <Button type="button" onClick={onFilterApply}>Хайх</Button>
              </Form.Field>
              <Form.Field className="report-filter-actions">
                <label>&nbsp;</label>
                <Button type="button" primary disabled={batchPrinting} onClick={printUnprintedBatch}>
                  Хэвлээгүй хүргэлтийн баримт
                </Button>
              </Form.Field>
            </Form>

            <div className="papa-select-toolbar margin-top-20">
              {!papaSelectMode ? <Button type="button" color="green" data-testid="papa-select-start" onClick={onStartPapaSelect}>Papa хүргэлт үүсгэх</Button> : <React.Fragment>
                <span className="papa-select-step-label">1. Хүргэлт сонгох</span>
                {selectedDeliveryIds.size > 0 ? <React.Fragment>
                  <span className="papa-select-step-label">2. Дүүрэг сонгох</span>
                  <Dropdown
                    data-testid="papa-district-select"
                    placeholder="Дүүрэг сонгох"
                    selection
                    search
                    loading={papaDistrictsLoading}
                    options={papaDistricts.map((district) => ({
                      key: district.id,
                      value: district.id,
                      text: district.short_name ? `${district.name} (${district.short_name})` : district.name,
                    }))}
                    value={papaDistrictId || undefined}
                    onChange={(_e, data) => setPapaDistrictId(String(data.value || ''))}
                  />
                </React.Fragment> : null}
                <Button type="button" color="green" data-testid="papa-select-create" disabled={selectedDeliveryIds.size === 0 || !papaDistrictId || papaCreating} loading={papaCreating} onClick={onCreatePapaSelected}>Сонгосоныг үүсгэх ({selectedDeliveryIds.size})</Button>
                <Button type="button" data-testid="papa-select-cancel" onClick={onCancelPapaSelect} disabled={papaCreating}>Болих</Button>
              </React.Fragment>}
            </div>

            {successMsg ? <div className="success-msg" data-testid="papa-create-success">{successMsg}</div> : null}
            {errorMsg ? <div className="error-msg" data-testid="papa-create-error">{errorMsg}</div> : null}

            <Table striped className="admin-list-table" width={16}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>№</Table.HeaderCell>
                  <Table.HeaderCell>Баримтын дугаар</Table.HeaderCell>
                  <Table.HeaderCell>Хэвлэлт</Table.HeaderCell>
                  <Table.HeaderCell>Нийт дүн</Table.HeaderCell>
                  <Table.HeaderCell>Төлөв</Table.HeaderCell>
                  <Table.HeaderCell>Худалдан авсан сувгийн төлөв</Table.HeaderCell>
                  <Table.HeaderCell>Захиалгын огноо</Table.HeaderCell>
                  {/* <Table.HeaderCell>Хүчинтэй хугацаа</Table.HeaderCell> */}
                  <Table.HeaderCell>Хэрэглэгч</Table.HeaderCell>
                  <Table.HeaderCell>Байгууллага</Table.HeaderCell>
                  <Table.HeaderCell>Дэлгэрэнгүй</Table.HeaderCell>
                  <Table.HeaderCell>Засвар</Table.HeaderCell>
                  <Table.HeaderCell></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              
              <Table.Body>
                {visibleOrders.map((order: any) => {
                  const selectableDeliveries = papaSelectMode
                    ? (order.deliveries || []).filter((delivery: any) => !order.pickup && canSelectDeliveryForPapa(delivery))
                    : (order.deliveries || [])
                  return <React.Fragment key={"order-" + order.id}>
                    <Table.Row className="order-list-row" style={{ backgroundColor: 'var(--admin-table-row-muted)' }}>
                      <Table.Cell className="underline cursor" data-label="№">
                        <a target="_blank" href={`/mongol/report/order/${order.id}`}><strong>#{order.id}</strong></a>
                      </Table.Cell>
                      <Table.Cell className="cursor" data-label="Токен">
                        <a target="_blank" href={`/mongol/report/order/${order.id}`}>
                        <strong>{order.token}</strong>
                        {order.store === 1 ? <span> (Дэлгүүр)</span> : null}
                        {order.express ? <span data-testid={`order-express-tag-${order.id}`}> (Express)</span> : null}
                        {order.pickup ? <span data-testid={`order-pickup-tag-${order.id}`}> (Очиж авах)</span> : null}
                        </a>
                      </Table.Cell>
                      <Table.Cell className="print-count-cell" data-label="Хэвлэлт">
                        <a className="print-count-link" target="_blank" href={`/mongol/report/order/${order.id}`}>
                          <Icon className="print-count-icon" name="print" />
                          <strong>{Number(order?.receiptCount) || 0}</strong>
                        </a>
                      </Table.Cell>
                      <Table.Cell className="cursor" data-label="Нийт дүн" onClick={() => toggle(order.id)}>
                        <NumberFormat displayType={'text'} value={order.totalAmount} suffix="₮" thousandSeparator={true}/>
                      </Table.Cell>
                      <Table.Cell className={`cursor order-status ${String(order.status || '').toLowerCase()}`} 
                        data-label="Төлөв"
                        onClick={() => toggle(order.id)}>
                        {parseStatus(order.status)} {order.payLater ? '(клуб гишүүн)' : ''}
                      </Table.Cell>
                      <Table.Cell className="cursor" data-label="Худалдан авсан сувгийн төлөв" onClick={() => toggle(order.id)}>
                        <span className={paymentChannelClassName(order.paymentChannel)}>{parsePaymentChannel(order.paymentChannel)}</span>
                      </Table.Cell>
                      <Table.Cell data-label="Захиалгын огноо">
                        { order.createdAtStr || '-' }
                      </Table.Cell>
                      {/* <Table.Cell>
                        { order.reserveDate || '-' }
                      </Table.Cell> */}
                      <Table.Cell data-label="Хэрэглэгч">
                        <Link className="underline" to={"/mongol/client/" + order.clientId}>{ order.ownName }</Link>
                      </Table.Cell>
                      <Table.Cell data-label="Байгууллага">
                        <Link className="underline" to={"/mongol/company/" + order.companyId}>{ order.companyName }</Link>
                      </Table.Cell>
                      <Table.Cell className="cursor" data-label="Дэлгэрэнгүй">
                        <a target="_blank" href={`/mongol/report/order/${order.id}`}>
                          <strong><Icon name="folder open outline"/></strong>
                        </a>
                      </Table.Cell>
                      <Table.Cell data-label="Засвар">
                        {order.status === 'CONFIRMED' && order.hasBankPayment ? (
                          <small data-testid={`order-edit-locked-${order.id}`}>{BANK_PAID_EDIT_LOCKED_MSG}</small>
                        ) : order.status === 'CONFIRMED' ? (
                          <Button
                            type="button"
                            size="mini"
                            data-testid={`order-edit-btn-${order.id}`}
                            onClick={() => setEditOrder(order)}
                          >
                            Засварлах
                          </Button>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell className="cursor" data-label="Задлах" onClick={() => toggle(order.id)}>
                        { order.showItems ? <Icon name="minus"/> : <Icon name="plus"/> }
                      </Table.Cell>
                    </Table.Row>
                    { order.showItems ? <React.Fragment>
                      <Table.Row>
                        <Table.Cell colSpan={12} data-label="Хаяг">
                          <strong className="col-top">Хаяг: </strong>
                          <span>{order.town}, {order.district}, {order.unit}, {order.street}</span>
                        </Table.Cell>
                      </Table.Row>
                      {selectableDeliveries.map((delivery: any) => {
                        return <OrderDeliveryComponent
                          key={delivery.id}
                          delivery={delivery}
                          colSpan={12}
                          papaSelectMode={papaSelectMode}
                          papaSelected={selectedDeliveryIds.has(delivery.id)}
                          onPapaSelectToggle={onPapaSelectToggle}
                          papaSelectable={order.status === 'CONFIRMED' && !order.pickup && canSelectDeliveryForPapa(delivery)}
                        />
                      })}
                      <Table.Row>
                        <Table.Cell colSpan={12} data-label="Дүн">
                          <div className="order-summary">
                            <div>Барааны нийт үнэ: <NumberFormat displayType="text" value={order.totalAmount - (order.totalDeliveryCost || 0)} suffix="₮" thousandSeparator={true} /></div>
                            <div>Хүргэлтийн нийт үнэ: <NumberFormat displayType="text" value={order.totalDeliveryCost || 0} suffix="₮" thousandSeparator={true} /></div>
                            {order.express ? <div>Үүнээс Express ({order.expressZone}): <NumberFormat displayType="text" value={order.expressFee || 0} suffix="₮" thousandSeparator={true} /></div> : null}
                            <div>Барааны хүргэлтийн зардал: <NumberFormat displayType="text" value={order.itemsDeliveryCost || 0} suffix="₮" thousandSeparator={true} /></div>
                            <div>Нийт үнэ: <NumberFormat displayType="text" value={order.totalAmount} suffix="₮" thousandSeparator={true} /></div>
                          </div>
                        </Table.Cell>
                    </Table.Row>
                    </React.Fragment> : null
                    }
                  </React.Fragment>
                })}
                {papaSelectMode && visibleOrders.length === 0 ? <Table.Row>
                  <Table.Cell colSpan={11} textAlign="center">
                    Papa хүргэлт үүсгэх боломжтой захиалга олдсонгүй
                  </Table.Cell>
                </Table.Row> : null}
              </Table.Body>
            </Table>

            <OrderEditModal
              open={!!editOrder}
              orderId={editOrder?.id}
              deliveries={editOrder?.deliveries || []}
              onClose={() => setEditOrder(null)}
              onSaved={() => fetchOrders(page.page)}
            />

            <div className="order-summary-section flex-div justify-content-center align-items-center" key={`summary-${totalAmount}-${totalOrderCount}-${totalDeliveryCost}`}>
              <div className="order-summary-content flex-div align-items-center">
                <div className="summary-item">
                  <strong>Амжилттай захиалгын тоо: </strong>
                  <span>{totalOrderCount.toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <strong>Нийт дүн (Амжилттай захиалгууд): </strong>
                  <NumberFormat displayType="text" value={totalAmount} suffix="₮" thousandSeparator={true} key={`total-${totalAmount}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт хүргэлтийн зардал: </strong>
                  <NumberFormat displayType="text" value={totalDeliveryCost} suffix="₮" thousandSeparator={true} key={`delivery-${totalDeliveryCost}`} />
                </div>
                <div className="summary-item">
                  <strong>Нийт барааны ширхэг (амжилттай): </strong>
                  <span>{totalProductPieces.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className={ADMIN_LIST_PAGINATION_ROW_CLASS}>
              <Pagination activePage={page.page} totalPages={page.totalPages} onPageChange={onPageChange}></Pagination>
              <div className="page-size-selector flex-div justify-content-center align-items-center">
                <div className='flex-div align-items-center'>ХУУДАСНЫ ХЭМЖЭЭ:</div>
                <Dropdown
                  selection
                  options={[
                    { key: 10, text: '10', value: 10 },
                    { key: 20, text: '20', value: 20 },
                    { key: 50, text: '50', value: 50 },
                    { key: 100, text: '100', value: 100 }
                  ]}
                  value={pageSize}
                  onChange={onPageSizeChange}
                />
              </div>
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

export default ReportOrders;